"use client"

import { ArrowLeft, ChevronDown, ChevronUp, Printer } from "lucide-react"
import { useRouter } from "next/navigation"
import type React from "react"
import { useRef, useState } from "react"

import { Button } from "@/core/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/core/ui/collapsible"
import { cn } from "@/core/utils/utils"

interface PolicyLayoutProps {
  title: string
  lastUpdated: string
  children: React.ReactNode
  toc?: { title: string; id: string }[]
}

export function PolicyLayout({ title, lastUpdated, children, toc }: PolicyLayoutProps) {
  const router = useRouter()
  const contentRef = useRef<HTMLDivElement>(null)
  const [isTocOpen, setIsTocOpen] = useState(false)

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950/50 flex justify-center py-8 md:py-12 relative">
      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-size-[24px_24px] mask-[radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

      {/* A4 Paper Layout */}
      <div 
        ref={contentRef}
        className={cn(
          "w-full max-w-[210mm] min-h-[297mm] mx-4 md:mx-0",
          "bg-background text-foreground",
          "shadow-2xl border border-border/20",
          "relative z-10 print:shadow-none print:border-none print:mx-0 print:w-full print:max-w-none"
        )}
      >
        {/* Header Actions (Inside Layout) */}
        <div className="absolute top-6 left-8 z-30 print:hidden">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors gap-2"
            onClick={() => router.back()}
            title="Go Back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wider hidden sm:inline-block">Back</span>
          </Button>
        </div>

        <div className="absolute top-6 right-8 z-30 print:hidden">
           <Button
            variant="ghost"
            size="sm"
            className="rounded-full hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors gap-2"
            onClick={handlePrint}
            title="Print Document"
          >
            <span className="text-xs font-medium uppercase tracking-wider hidden sm:inline-block">Print</span>
            <Printer className="h-4 w-4" />
          </Button>
        </div>

        {/* Glass Header */}
        <div className="sticky top-0 z-20 w-full bg-background/90 backdrop-blur-xl border-b border-border/10 px-8 py-12 print:static print:bg-transparent print:border-b-2 print:border-black">
          <div className="flex flex-col items-center justify-center text-center space-y-4 pt-4">
            <h1 className="text-4xl md:text-5xl font-thin tracking-tight text-foreground">
              {title}
            </h1>
            <div className="h-1 w-24 bg-linear-to-r from-transparent via-premium-cyan/50 to-transparent" />
          </div>
        </div>

        {/* Content Area */}
        <div className="px-8 py-12 md:px-16 md:py-16">
          
          {/* Metadata Row */}
          <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-border/10 pb-8 mb-12 gap-4">
            <div className="flex items-center gap-2">
               <div className="h-2 w-2 rounded-full bg-premium-cyan animate-pulse" />
               <span className="text-sm font-medium tracking-wide uppercase text-muted-foreground">Legal Document</span>
            </div>
            <div className="text-sm font-light text-muted-foreground">
              Last Updated: <span className="font-medium text-foreground">{lastUpdated}</span>
            </div>
          </div>

          {/* Collapsible Table of Contents */}
          {toc && toc.length > 0 && (
            <Collapsible
              open={isTocOpen}
              onOpenChange={setIsTocOpen}
              className="mb-16 border rounded-lg border-border/10 bg-muted/5 print:hidden"
            >
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full flex items-center justify-between p-4 hover:bg-muted/10 font-medium text-sm tracking-wide uppercase text-muted-foreground">
                  <span>Table of Contents</span>
                  {isTocOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-6 pb-6 pt-2">
                 <nav className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                  {toc.map((item) => (
                    <a 
                      key={item.id} 
                      href={`#${item.id}`}
                      className="text-sm text-muted-foreground/80 hover:text-foreground transition-colors hover:translate-x-1 duration-200 flex items-center gap-2"
                      onClick={(_e) => {
                         // Optional: smooth scroll handling if needed, but native anchor works
                      }}
                    >
                      <span className="h-1 w-1 rounded-full bg-border" />
                      {item.title}
                    </a>
                  ))}
                </nav>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Main Content */}
          <article className={cn(
            // Base Typography
            "prose prose-zinc dark:prose-invert max-w-none",
            
            // Headings
            "prose-headings:font-normal prose-headings:tracking-tight prose-headings:text-foreground",
            "prose-h2:text-2xl prose-h2:mt-16 prose-h2:mb-8 prose-h2:border-l-[3px] prose-h2:border-premium-cyan/50 prose-h2:pl-6",
            "prose-h3:text-lg prose-h3:mt-10 prose-h3:mb-4 prose-h3:font-medium prose-h3:text-foreground/90",
            
            // Paragraphs & Text
            "prose-p:text-zinc-700 dark:prose-p:text-zinc-300 prose-p:leading-8 prose-p:font-normal prose-p:tracking-normal prose-p:mb-6 prose-p:text-justify",
            "prose-strong:font-bold prose-strong:text-foreground",
            
            // Lists
            "prose-ul:my-6 prose-ul:list-none prose-ul:pl-0",
            "prose-li:relative prose-li:pl-6 prose-li:mb-3 prose-li:text-zinc-700 dark:prose-li:text-zinc-300 prose-li:font-normal",
            "prose-li:before:content-[''] prose-li:before:absolute prose-li:before:left-0 prose-li:before:top-[0.6em] prose-li:before:h-1.5 prose-li:before:w-1.5 prose-li:before:rounded-full prose-li:before:bg-premium-cyan",
            
            // Links
            "prose-a:text-foreground prose-a:font-medium prose-a:no-underline prose-a:border-b prose-a:border-premium-violet/30 hover:prose-a:border-premium-violet hover:prose-a:text-premium-violet transition-all duration-200",
            
            // Blockquotes
            "prose-blockquote:border-l-4 prose-blockquote:border-premium-violet/20 prose-blockquote:bg-muted/10 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:italic prose-blockquote:text-zinc-600 dark:prose-blockquote:text-zinc-400",

            // Print Overrides
            "print:prose-p:text-black print:prose-headings:text-black print:prose-a:text-black print:prose-a:border-black"
          )}>
            {children}
          </article>

          {/* Glassy Separator */}
          <div className="my-20 h-px w-full bg-linear-to-r from-transparent via-border to-transparent opacity-50" />

          {/* End Section */}
          <div className="flex flex-col items-center justify-center text-center space-y-4">
             <div className="h-8 w-8 rounded-full bg-linear-to-tr from-premium-violet via-premium-cyan to-premium-pink opacity-20 blur-xl" />
            <p className="text-xs font-light tracking-[0.2em] uppercase text-muted-foreground/60">
              End of Document
            </p>
            <p className="text-sm font-medium text-foreground">
              Hamid Ayub Portfolio
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
